import { FabricDiagramStateHolder } from "../../modules/FabricDiagramStateHolder";
import { DIAGRAM_EVENTS } from "../../diagram/config/events.const";
import { GridConfig } from "./modules/GridConfig";
import { CellSelection } from "./modules/selection/CellSelection";
import { ScrollHandler } from "./modules/ScrollHandler";
import { PluginDragHandler } from "./modules/PluginDragHandler";
import { GRID_CELL_CLICKABLE_ELEMENTS } from "./components/cell/grid-cell.config";
import { MouseMoveSelectedCell } from "./modules/MouseMoveSelectedCell";
import { ConnectionLineDrag } from "./modules/ConnectingLineDrag";
import { ConnectingLineHandler } from "./modules/ConnectingLineHandler";
import { LabelCell } from "./components/label/label-cell";
import { keyboard } from "../../diagram/models/keycodes.enum";
import { LabelHandler } from "./modules/LabelHandler";
import { LineSelection } from "./modules/selection/LineSelection";
import { DiagramEventEmitter } from "../../diagram/modules/DiagramEventEmitter";
import { DIAGRAM_EMIT_EVENTS } from "../../diagram/modules/DiagramEvents";
import { changeLabelsPosition } from "./components/label/label-cell.utils";
import { CellIndependentPluginHandler } from "./modules/cellIndependentHandlers/CellIndependentPluginHandler";
import { ExpandablePluginHandler } from "./modules/cellIndependentHandlers/ExpandablePluginHandler";
import { CellIndependentMoveHandler } from "./modules/cellIndependentHandlers/CellIndependentMoveHandler";
import { GRID_DIRECTION } from "./const/grid-directions.const";
import { ViewDataRetrieval } from "./modules/ViewDataRetrieval";
import { APIHandler } from "./modules/APIHandler";
import { CameraViewUtils } from "./utils/CameraViewUtils";
import { ExpandingViewUtils } from "./utils/ExpandingViewUtils";
import { GridSizedViewUtils } from "./utils/GridSizeViewUtils";
import { CellHighlightHandler } from "./utils/CellHighlightHandler";
import { PluginRetrievalUtils } from "./utils/PluginRetrievalUtils";
import { DroppedPluginCreator } from "./utils/DroppedPluginCreator";
import { GridDrawingUtils } from "./utils/GridDrawingUtils";
import { CellRetrievalUtils } from "./utils/CellRetrievalUtils";
import { SelectionWrapper } from "./modules/selection/SelectionWrapper";
import { AnchoredPluginsHandler } from "./modules/AnchoredPluginsHandler";

export const GridView = function () {
    const instance = {}; // saved inside the closure to mentain the instances
    class GridView {
    instanceUuid; // the diagram instance uuid is recieved on the constructor

    /** will hold the @GridConfig instance. Used to define the cellWidth, cellHeight and other grid related settings. */
    config = null;

    /** will hold the @CellSelection instance. Handles Cell selection */
    cellSelection = null;

    /** will hold the @LineSelection instance. Handles line selection */
    lineSelection = null;

    /** will hold the @DiagramEventEmitter instance. Used to dispatch events on the diagram  */
    eventEmitter = null;

    /** will hold the @MouseMoveSelectedCell instance. Shows the drag arrow to create a connector */
    mouseMoveSelectedCellHandler = null;

    /** This is matrix used for the grid. It keeps a reference to the all @GridCell instances */
    generatedGrid = [];

    /** will hold the @CellIndependentPluginHandler instance. Handles the drop, creation of a cell indepenedent plugin, selection, addition of children,
     * removal of children, keeping track of the covered cells,
     * and everything else related to cell independent plugins.  */
    cellIndependentPluginHandler = null;

    /** will hold the @SelectionWrapper instance. This wraps CellSelection, LineSelection, cellIndependentHandler to unify them and handle the selection status of each more easily. */
    selectionWrapper = null;

    /** will hold the @ConnectingLineHandler instance. Keeps a reference to all cells that the connectors pass through.
     * When a cell redraws, that particular section of the connector that passes through the cell gets redrawn.
     * It uses a line clipping algorithm that gets the line section that intersects the particular cell.
     * Also has utils for removing, adding, updating connectors */
    connectingLineHandler = null;

    /** will hold the  @ConnectionLineDrag instance. Handles the dragging of a connector from a plugin to another and the creation of it after drop. */
    connectingLineDrag = null;

    /** will hold the  @ExpandablePluginHandler instance. This is used to handle the expansion of a cell independent plugin. */
    cellIndependentExpandable = null;

    /** will hold the  @AnchoredPluginsHandler instance. Handles plugins anchored to another plugin. Handles their selection status, addition, update, move.  */
    anchoredPluginsHandler = null;

    /** will hold the  @ScrollHandler instance. Handles different canvas events and acts upon them in relation to the camera and scrollbars. */
    scrollHandler;

    /** will hold the @LabelHandler instance. Deals with the label move, update, creation, deletion, etc  */
    labelHandler;

    /** will hold the @PluginDragHandler or @CellIndependentMoveHandler instance, depending on the currently moving element.
     * They handle the move of a element inside the grid from a position to another. */
    pluginDragHandler;

    /** Holds a reference to the diagram 2d context */
    canvasCtx;

    /** The variable used to define infromation about the currently dragged element.
     * It can either be dragged from the sidebar or a move action on a current element.
     * The obj looks like {x,y,width,height,hasOwnState}
     * x and y define the current position of the mouse.
     * width, height, hasOwnState are used for cell independent plugins only.
    */
    dragging = null;

    /** The variable used to define dataModel case.
    */
    dataModelDragging = null;

    /** This holds the initial grid size based on the dataset recieved on the initialization, or on the changeDataSet API Method */
    initialGridSize = {};

    /** A boolean value used to define if a label is being dragged or not  */
    draggingLabel = null;

    /** will hold the @DiagramCamera instance. This handles the scrollbars, zoom and the viewport. */
    camera;

    /**
     * The properties are set by the @initializeUtils method. Will hold a set of instances for different utils classes
     * utils.camera - Holds @CameraViewUtils instance. Contains camera related utils.
     * utils.expandingView - Holds @ExpandingViewUtils instance.  Contains util methods used for the expanding grid process
     * utils.gridSize - Holds @GridSizedViewUtils instance.
     * utils.cellHighlight - Holds @CellHighlightHandler instance. Handlers the cell highlighting during expanding, moving, expanding process
     * utils.pluginRetrieval - Holds @PluginRetrievalUtils instance. Handles plugin retrieval based on plugin uuid's
     * utils.droppedPluginCreator - Holds @DroppedPluginCreator instance. Handles plugin creation on drop
     * utils.gridDrawing - Holds @GridDrawingUtils instance. Util methods for drawing the view
     * utils.cellRetrieval - Holds @CellRetrievalUtils instance. Handles cell retrieval based on coordonates, or matrix index and more
     */
    utils = {};

    /**
     * Indicates if the initial grid draw happened
     */
    drawed = false;

    // autoscroll interval id reference used with the counter
    autoScrollIntervalId;

    // autoscroll interval used to verify the user hovered over  the side for more then x milisesconds. Currently set at 1500
    autoScrollCounter = 0;

    // the two variables defined in the animation function.
    animationsFunctionCtx = null;

    doubleClickActionOn = false;

    firstDragOverEventData = null; // container data - first DragOver event plugin from left sidebar - reset firstDragOverEventData on dropHandler

    stopLeftScroll = null; // stop left horizontal scroll

    isDataModelInstance = null; // boolean - true if Data Model Instance

    diagramStateHolder;

    cursorIsOutsideCanvas = false;

    /**
     * @constructor
     * @description This class is a singleton.
     *              If a instance exists for the provided diagram uuid then that is returned,
     *              otherwise a new instance is created, its dependencies are created based on the same instanceUuid(if they are singletons) and returned
     * @param { String } diagramInstanceUuid - Diagram UUID
     * @returns { GridView }
     */
    constructor(diagramInstanceUuid) {
        if (!instance[diagramInstanceUuid]) {
        // setting the singleton instance
            instance[diagramInstanceUuid] = this;
            this.instanceUuid = diagramInstanceUuid;
            this.config = new GridConfig(this.instanceUuid);
            this.cellSelection = new CellSelection(this.instanceUuid);
            this.lineSelection = new LineSelection(this.instanceUuid);
            this.eventEmitter = new DiagramEventEmitter(this.instanceUuid);
            this.mouseMoveSelectedCellHandler = new MouseMoveSelectedCell();
            this.generatedGrid = [];
            this.cellIndependentPluginHandler = new CellIndependentPluginHandler(this.instanceUuid);
            this.selectionWrapper = new SelectionWrapper(this.instanceUuid);
            this.connectingLineHandler = new ConnectingLineHandler(this.instanceUuid);
            this.connectingLineDrag = new ConnectionLineDrag(this);
            this.cellIndependentExpandable = new ExpandablePluginHandler(this.cellIndependentPluginHandler, this.instanceUuid);
            this.anchoredPluginsHandler = new AnchoredPluginsHandler(this.instanceUuid);
        }
        return instance[diagramInstanceUuid];
    }

    /**
     * @description Sets different dependencies needed by the grid view
     * @param { Object } canvasCtx  - canvas 2d context
     * @param { Object } dataHandler - data handler instance
     * @param { DiagramCamera } camera - diagram camera instance
     * @param { FabricDiagram } fabricDiagramCtx - current fabric diagram instance
     * @returns { Object } - Contains properties: x and y to be set as the initial camera position. Also contains grid view size properties
     */
    init(canvasCtx, dataHandler, camera, fabricDiagramCtx) {
        this.drawed = false;
        this.destroying = false;
        this.generatedGrid = [];
        this.initializeUtils();
        this.canvasCtx = canvasCtx;
        this.dataHandler = dataHandler;
        this.fabricDiagramCtx = fabricDiagramCtx;
        this.oneAtATime = fabricDiagramCtx.config.oneAtATime;
        this.readonly = fabricDiagramCtx.config.readonly;
        this.cellIndependentPluginHandler.setCtx(canvasCtx);
        this.selectionWrapper.setCellIndependentHandler(this.cellIndependentPluginHandler);
        this.camera = camera;
        this.connectingLineHandler.setCamera(camera);
        this.connectingLineHandler.addRedrawListener(this.cellIndependentPluginHandler);
        this.cellIndependentExpandable.setFabricDiagramContext(this.fabricDiagramCtx);
        this.anchoredPluginsHandler.setCellIndependentHandler(this.cellIndependentPluginHandler);
        this.anchoredPluginsHandler.setConnectingLineHandler(this.connectingLineHandler);
        this.scrollHandler = new ScrollHandler(this.camera);
        this.labelHandler = new LabelHandler(this.camera, this.instanceUuid, this.canvasCtx, this.generatedGrid);
        /* DELETE WHEN DONE DEVELOPMENT */ window.camera = this.camera;
        this.config.resetValuesToDefault();
        this.initialGridSize = this.config.calculateViewSize(dataHandler.data);
        this.animationsFunctionCtx = this.gridAnimations(this); // initializez the animationFrame loop

        const initialCamera = this.getInitialCameraPosition(camera);
        return { ...this.initialGridSize, ...initialCamera };
    }

    /**
     * @description Sets a bunch of util dependencies needed in GridView instance
     * @returns { void }
     */
    initializeUtils() {
        this.utils.camera = new CameraViewUtils();
        this.utils.expandingView = new ExpandingViewUtils();
        this.utils.gridSize = new GridSizedViewUtils();
        this.utils.cellHighlight = new CellHighlightHandler(this.instanceUuid);
        this.utils.pluginRetrieval = new PluginRetrievalUtils();
        this.utils.droppedPluginCreator = new DroppedPluginCreator();
        this.utils.gridDrawing = new GridDrawingUtils();
        this.utils.cellRetrieval = new CellRetrievalUtils();
    }

    /**
     * @description Draws the canvas background and
     * @param { Object } pos - lastViewport from DiagramCamera that represents the current Viewport.
     *                         Is used to prevent waisting processing time by preventing drawing elements that are outside the viewport.
     * @returns { void }
     */
    draw(pos, isDataModel) {
        this.connectingLineHandler.readonly = this.readonly;
        this.utils.gridDrawing.drawBackground(pos, this.canvasCtx, this.fabricDiagramCtx);
        this.drawGrid(isDataModel);
    }

    /**
     * @description - Returns the camera position that should be set as the initial camera position
     * @returns { Object } - Containing x and y properties that define the initial position of the camera to be set
     */
    getInitialCameraPosition() {
        const cfg = this.config.getGridCellCreationOptions();
        return {
            x: cfg.viewPadding - cfg.initialViewPadding,
            y: cfg.viewPadding - cfg.initialViewPadding
        };
    }

    /**
     * @description The intermediary for the api calls. Sends the data further to @APIHandler instance.
     * @param { String } action - The name of the action to be performed
     * @param { Object } payload - The payload for the current API call.
     * @returns { Any } - the value returned by the specific action handler call. Can be string, undefined, anything. Ussualy the UUID of the newly added plugin.
     */
    apiCallHandler(action, payload) {
        const apiHandler = this.getApiHandlerInstance();
        const diagramContent = this.getDiagramData();
        return apiHandler.apiCall(action, payload, this.generatedGrid, this, diagramContent);
    }

    /**
     * @description Returns a apiHandler instance
     * @returns { APIHandler }
     */
    getApiHandlerInstance() {
        return new APIHandler(this.labelHandler, this.cellIndependentPluginHandler, this.fabricDiagramCtx, this.camera, this.canvasCtx);
    }

    /**
     * @description - Change the diagram data displayed with the provided model as a parameter.
     *              - Resets all handlers
     * @param { Object } data - diagram object model
     * @returns The new diagram data.
     */
    changeDataSet(data, customData, isDataModel) {
        const apiHandler = this.getApiHandlerInstance();
        if (!this.diagramStateHolder) this.diagramStateHolder = new FabricDiagramStateHolder(this.instanceUuid);
        if (!this.isDataModelInstance && isDataModel) {
            this.isDataModelInstance = isDataModel;
            this.diagramStateHolder.isDataModel = true;
        }
        this.generatedGrid = [];
        this.labelHandler.labelsList = [];
        this.labelHandler.connectorsList = [];
        this.cellIndependentPluginHandler.plugins.clear();
        apiHandler.setNewLabelData(data, "pluginLabel");
        apiHandler.setNewLabelData(data, "connectorLabel");
        this.dataHandler.data = data;
        this.connectingLineHandler.resetingDataSet();
        this.connectingLineDrag.resetingDataSet();
        this.mouseMoveSelectedCellHandler.destroy();
        this.anchoredPluginsHandler.resetingDataSet();
        this.oneAtATime = this.fabricDiagramCtx.config.oneAtATime;
        this.readonly = this.fabricDiagramCtx.config.readonly;
        this.config.resetValuesToDefault();
        this.initialGridSize = this.config.calculateViewSize(this.dataHandler.data);
        this.camera.HEIGHT = this.initialGridSize.height;
        this.camera.WIDTH = this.initialGridSize.width;
        this.draw(this.camera.lastViewport, isDataModel);
        return this.getDiagramData();
    }

    /**
     * @description  Centers the grid on initialization inside the viewport
     * @returns { void }
     */
    setInitialCameraPosition() {
        const pos = this.config.getInitialCameraPosition(this.canvasCtx, this.generatedGrid);
        this.camera.switchCameraFocus(pos);
    }

    /**
     * @description - Draws the grid, canvas background, connectors and cell independent plugins
     * @returns { void }
     */
    drawGrid(isDataModel) {
        const options = this.config.getGridCellCreationOptions();
        if (this.generatedGrid.length === 0 && !this.temporary) {
            const opt = this.dataHandler.data ? this.config.calculateViewSize(this.dataHandler.data) : options;
            this.utils.gridDrawing.drawVisibleInitialGrid(this.generatedGrid, opt, this.camera.scrollbars, this.canvasCtx, this.labelHandler);
            this.utils.gridDrawing.setInitialGridContent(this.generatedGrid, this.fabricDiagramCtx, this.config, this.dataHandler.data, this.camera.scrollbars, this.cellIndependentPluginHandler, this.canvasCtx, this.readonly, this.anchoredPluginsHandler, isDataModel);
            this.utils.gridDrawing.setInitialGridConnectors(this.connectingLineHandler, this.dataHandler.data, this.generatedGrid, this.config, this.labelHandler);
            if (!this.drawed) {
                this.drawed = true;
                this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.VIEW_MODEL_LOADED, { data: this.getDiagramData() }, true);
            }
            this.setInitialCameraPosition();
        } else {
            this.utils.gridDrawing.clearCanvas(this.camera.lastViewport, this.canvasCtx);
            this.utils.gridDrawing.drawBackground(this.camera.lastViewport, this.canvasCtx, this.fabricDiagramCtx);
            this.utils.gridDrawing.drawVisibleGrid(this.generatedGrid, options, this.camera.scrollbars);
            this.utils.gridDrawing.drawCellIndependentPlugins(this.cellIndependentPluginHandler, this.camera.scrollbars);
        }
    }

    /**
     * @description This methods retrieves the diagram object model
     * @returns { Object } - the diagram object model
     */
    getDiagramData() {
        const dataRetrieval = new ViewDataRetrieval(this.generatedGrid, this.camera, this.cellIndependentPluginHandler, this.anchoredPluginsHandler, this.labelHandler, this.canvasCtx);
        return dataRetrieval.getData();
    }

    /**
     * @description Clears current temporary grid and recreates it. Shifts current elements and camera view if necesary
     * @param { Object } pos -  object containing {x, y} properties representing the current mouse position
     * @returns { GridCell[] } - An array containing all the newly generated temporary cells
     */
    createTemporaryGrid(pos) {
        const options = this.config.getGridCellCreationOptions();
        const permanentGrid = this.temporary && this.temporary.permanentGrid ? this.temporary.permanentGrid : this.generatedGrid;
        let dir = this.utils.gridSize.getMouseDirectionFromTarget(pos, permanentGrid);
        let size = this.utils.expandingView.calculateSizeToGenerate(pos, options, permanentGrid, dir, this);
        let temporaryCellsNr = null;
        let temporaryRowsNr;
        let totalRowWidth;
        const backupSizeDir = size.dir;
        size = size.size;
        this.clearTemporaryGrid();
        this.createTemporaryObject(dir, size);

        temporaryCellsNr = Math.floor(this.temporary.size.width / options.cellWidth);
        temporaryRowsNr = Math.floor(this.temporary.size.height / options.cellHeight);
        // Drag la dreapta
        if (temporaryRowsNr === 0 && temporaryCellsNr > 0 && backupSizeDir === "RIGHT" && !dir) {
            dir = GRID_DIRECTION.RIGHT;
            this.temporary.dir = GRID_DIRECTION.RIGHT;
        }
        // New camera position
        this.temporary.prevCamera.left = this.camera.scrollbars.left;
        this.temporary.prevCamera.top = this.camera.scrollbars.top;

        // Increase world view
        const previousWorldSize = { width: this.camera.WIDTH, height: this.camera.HEIGHT };
        this.temporary.previousWorldView = previousWorldSize;
        const toSet = {
            width: this.temporary.previousWorldView.width + this.temporary.size.width,
            height: this.temporary.previousWorldView.height + this.temporary.size.height
        };
        this.camera.setWorldView(toSet);

        const cellMove = this.utils.expandingView.getCurrentCellsMovement(this.temporary.size, dir);
        if (dir !== GRID_DIRECTION.RIGHT) {
            this.utils.gridSize.changeConnectorsPosition(cellMove, this.connectingLineHandler.connectors);
            this.utils.gridSize.changeCurrentCellsPositions(cellMove, this.generatedGrid);
            changeLabelsPosition(cellMove, this.labelHandler.labelsList);
            this.utils.gridSize.changeCellIndependentPluginsPosition(cellMove, this.cellIndependentPluginHandler.plugins);
        }
        this.temporary.permanentGrid = this.generatedGrid.map(row => Object.assign([], row.map(col => ({ ...col }))));

        totalRowWidth = this.generatedGrid[0].length + temporaryCellsNr;
        let newGeneratedRowCells = [];
        let newGeneratedCollCells = [];
        if (temporaryRowsNr) {
            newGeneratedRowCells = this.utils.expandingView.generateAditionalRows(temporaryRowsNr, this.generatedGrid, dir, totalRowWidth, options, this.canvasCtx, this.labelHandler);
        }
        if (temporaryCellsNr) {
            newGeneratedCollCells = this.utils.expandingView.generateAditionalCells(temporaryCellsNr, this.generatedGrid, dir, totalRowWidth, options, this.canvasCtx, this.labelHandler);
        }
        // Moving the camera
        const cameraToSet = this.utils.camera.decideCameraPosition(dir, this.camera, cellMove, this.temporary.prevCamera);

        this.camera.switchCameraFocus(cameraToSet);

        return [newGeneratedRowCells, newGeneratedCollCells].flat();
    }

    /**
     * @description - Creates the temporary config object
     * @param { String } dir - The direction in which the temporary grid is being generated
     * @param { Object } size - {width, height} the size of the temporary grid
     * @return { void }
     */
    createTemporaryObject(dir, size) {
        this.temporary = {
            dir,
            size,
            prevCamera: {}
        };
    }

    /**
     * @description clears the temporary object
     * @returns { void }
     */
    clearTemporaryObject() {
        this.temporary = null;
    }

    /**
     * @description clears the temporary cells, shifts the camera and moves the permanent cells and their content on the original position
     * @returns { void }
     */
    clearTemporaryGrid() {
        if (this.temporary) {
            let negativeSize = this.utils.expandingView.getTemporaryNegativeSize(this.temporary.size);
            this.generatedGrid = this.utils.gridSize.removeTemporaryCells(this.generatedGrid);
            negativeSize = this.utils.expandingView.getCurrentCellsMovement(negativeSize, this.temporary.dir);

            this.utils.gridSize.changeConnectorsPosition(negativeSize, this.connectingLineHandler.connectors);
            this.utils.gridSize.changeCurrentCellsPositions(negativeSize, this.generatedGrid);
            changeLabelsPosition(negativeSize, this.labelHandler.labelsList);
            this.utils.gridSize.changeCellIndependentPluginsPosition(negativeSize, this.cellIndependentPluginHandler.plugins);

            // Decrease world view
            this.camera.setWorldView(this.temporary.previousWorldView);
            // New camera position
            if (this.temporary.dir !== "RIGHT") {
                if (this.temporary.prevCamera.left <= 0) {
                    this.temporary.prevCamera.left = 1;
                }
                if (this.temporary.prevCamera.top <= 0) {
                    this.temporary.prevCamera.top = 1;
                }
                this.camera.switchCameraFocus(this.temporary.prevCamera);
            }
            if (this.temporary.dir === "RIGHT") {
                this.camera.draw();
            }
            this.clearTemporaryObject();
        }
    }

    /**
     * @description The function gets called by FabricDiagram when a event is happening and sends it to its handler based on the event param.
     * @param {String} event - THe event name
     * @param {Object | String} payload - The event data
     * @returns { void }
     */
    eventHappening(event, payload) {
        if (event === DIAGRAM_EVENTS.MOUSE_MOVE) {
            this.mouseMoveHandler(payload);
        } else if (event === DIAGRAM_EVENTS.DRAG_OVER) {
            if (this.readonly) return;
            this.dragOverHandler(payload);
        } else if (event === DIAGRAM_EVENTS.DROP) {
            if (this.readonly) return;
            this.dropHandler(payload);
        } else if (event === DIAGRAM_EVENTS.DRAG_LEAVE) {
            if (this.readonly) return;
            this.dragLeaveHandler();
        } else if (event === DIAGRAM_EVENTS.CLICK) {
            this.mouseClickHandler();
        } else if (event === DIAGRAM_EVENTS.MOUSE_DOWN) {
            this.mouseDownHandler(payload);
        } else if (event === DIAGRAM_EVENTS.MOUSE_LEAVE) {
            if (this.readonly) return;
            this.cursorIsOutsideCanvas = true;
        } else if (event === DIAGRAM_EVENTS.MOUSE_ENTER) {
            this.cursorIsOutsideCanvas = false;
        } else if (event === DIAGRAM_EVENTS.MOUSE_UP_WINDOW) {
            this.cursorIsOutsideCanvas = false;
            this.mouseUpWindowHandler(payload);
        } else if (event === DIAGRAM_EVENTS.MOUSE_UP) {
            this.mouseUpHandler(payload);
        } else if (event === DIAGRAM_EVENTS.KEYDOWN) {
            if (payload && payload.keyCode === keyboard.DELETE) {
                if (this.readonly) return;
                this.keyDownHandlerDelete(payload);
            }
        } else if (event === DIAGRAM_EVENTS.MOUSE_DBL_CLICK) {
            this.mouseDblClickHandler(payload);
        } else if (event === DIAGRAM_EVENTS.MOUSE_WHEEL) {
            this.mouseWheelHandler();
        }

        if (this.draggingLabel || this.connectingLineDrag.activeLineDrag() || this.cellIndependentExpandable.isExpanding()) { return; }
        this.scrollHandler.eventHappened(event, payload, this.cellIndependentPluginHandler);
    }

    /**
     * @description mouse click event handler
     * @returns { void }
     */
    mouseClickHandler() {

    }

    /**
     * @description Clears the view, all references and instances created for this instance id
     * @param {String} - the current diagram's instance id
     * @returns {void}
     */
    clearView(instanceUuid) {
        this.destroying = true;
        this.generatedGrid = [];
        this.connectingLineHandler.destroy(instanceUuid);
        // this.connectingLineHandler = null;
        this.cellSelection.destroy(instanceUuid);
        this.config.destroy(this.instanceUuid);
        this.lineSelection.destroy(instanceUuid);
        this.selectionWrapper.destroy(instanceUuid);
        this.anchoredPluginsHandler.destroy(instanceUuid);
        instance[instanceUuid] = null;
        delete instance[instanceUuid];
    }

    /**
     * @description - Mouse wheel handler
     */
    mouseWheelHandler() {
        // checks to see if the cell independent menu is open and closes it if it is
        const headerInstance = this.cellIndependentPluginHandler.checkForOpenedMenu(); // close header menu in Data Modelv
        if (headerInstance) {
            headerInstance.closeImportExportMenu();
        }

        if (this.isDataModelInstance) {
        // check If More items opened
            const moreItems = document.getElementById("moreItemsListId");
            if (moreItems) {
                moreItems.remove();
            }
        }
    }

    /**
     * @description - Mouse move event handler
     * @param { Object } payload - Object {x, y} - indicating the current mouse position
     * @returns { void }
     */
    mouseMoveHandler(payload) {
        const mousePos = { x: payload.x, y: payload.y };
        if (this.draggingLabel) { // handles the scenario where a label is being dragged
            const dataTest = this.cellSelection.updatePreviouslySelectedCell(this.generatedGrid);
            this.labelHandler.mouseMoveLabel(payload, this, this.generatedGrid, this.connectingLineHandler);
            this.mouseMoveSelectedCellHandler.resetHover();
        } else {
        /** informs @MouseMoveSelectedCellHandler instance of the mouse move action to act accordingly */
            this.mouseMoveSelectedCellHandler.mouseMoveHappened(this.cellSelection.getSelected(), mousePos, this.generatedGrid);
            if (this.connectingLineDrag.activeLineDrag()) {
                this.sidesDragScroll(payload);
                this.connectingLineDrag.updateLine(mousePos, this.utils.cellRetrieval.getCellPosition(mousePos, this.temporary, this.generatedGrid));
            }
            // checks to see if there is currently a cell independent plugin expanding
            /** if yes, it sends the payload to @ExpandablePluginHandler to handle the expand */
            if (this.cellIndependentExpandable.isExpanding()) {
                const permaGrid = this.temporary && this.temporary.permanentGrid ? this.temporary.permanentGrid : this.generatedGrid;
                this.cellIndependentExpandable.mouseMove(mousePos, this.temporary, this.generatedGrid, permaGrid);
                this.dataModelDragging = true;
                const setConfig = { width: this.config.cellWidth, height: this.config.cellHeight };
                this.dragging = { ...mousePos, setConfig, hasOwnState: true };
            }

            // checks to see if there is a element dragging inside the diagram
            if (this.scrollHandler.isDragging) {
                if (this.pluginDragHandler && this.pluginDragHandler instanceof CellIndependentMoveHandler) {
                    this.cellIndependentPluginHandler.mouseMoveHandler(payload, this.pluginDragHandler.draggedPlugin, this.generatedGrid);
                    if (this.pluginDragHandler.mouseLeftDraggedPluginInitialPosition(payload)) {
                        this.dragging = { ...mousePos, ...this.pluginDragHandler.getSize(), hasOwnState: true };
                    } else this.dragging = null;
                } else {
                    this.dragging = mousePos;
                }
                // checks to see if the mouse position is on the side of the diagram view
                // this.sidesDragScroll(payload);
            }
        }
    }

    /**
     * @description Handles mouse up events
     * @param { Object } payload - {x,y} object that indicates the current mouse position
     * @returns { void }
     */
    mouseUpHandler(payload) {
        const diagramContent = this.isDataModelInstance ? this.getDiagramData() : null;
        // indicates to the cell independent plugin handler that the mouse up event happened
        this.cellIndependentPluginHandler.mouseup();
        if (this.cellIndependentExpandable.isExpanding()) { // check to see if there was a expanding going on
        // trigger mouseUp inside cell independent expandable instance
            this.cellIndependentExpandable.mouseUp(this.cellIndependentPluginHandler, payload, this.generatedGrid);
        }
        if (this.draggingLabel) { // checks to see if a label is being dragged
            this.labelHandler.setLabelNewPosition(payload, this.camera);
            this.draggingLabel = null;
            const dataTest = this.cellSelection.updatePreviouslySelectedCell(this.generatedGrid);
            this.mouseMoveSelectedCellHandler.resetHover();
            this.scrollHandler.isDragging = false;
        } else { // checks to see if a label is being dragged
            if (this.isDataModelInstance ? !this.isDataModelInstance : (this.connectingLineDrag.activeLineDrag() && !this.readonly)) {
                this.connectingLineDrag.endDrag(payload, this.utils.cellRetrieval.getCellPosition(payload, this.temporary, this.generatedGrid), this.labelHandler);
            }
            if (this.dataModelDragging) { // checks to see if dataModel expanding class outside grid - generate new grid
                if (this.temporary) {
                    this.utils.gridSize.persistTemporaryCells(this.generatedGrid);
                    this.clearTemporaryObject();
                }
                this.dataModelDragging = null;
                this.dragging = null;
            }
            // checking to see if a cell independent element is dragging over the canvas
            if (this.scrollHandler.isDragging && this.pluginDragHandler && this.pluginDragHandler instanceof CellIndependentMoveHandler && !this.readonly) {
                this.pluginDragHandler.dropElement(this.dragging, this.generatedGrid, this.cellIndependentPluginHandler, this.canvasCtx, diagramContent);
                this.dragging = null;
                this.pluginDragHandler = null;
                this.scrollHandler.isDragging = null;
                if (this.temporary) {
                    this.utils.gridSize.persistTemporaryCells(this.generatedGrid);
                    this.clearTemporaryObject();
                }// checking to see if a cell based element is dragging over the canvas
            } else if (this.scrollHandler.isDragging && this.pluginDragHandler && !this.readonly) {
                this.pluginDragHandler.dropCell({ x: payload.x, y: payload.y }, this.temporary, this.generatedGrid, diagramContent);
                Object.assign([], this.generatedGrid).flat(Infinity).forEach(cell => cell.clearSpecificCustomDrawingFunc("TEMP_BORDER_DRAWING"));
                this.labelHandler.updateConnectorLabelPosition(this.connectingLineHandler, this.generatedGrid, this.cellIndependentPluginHandler, this.canvasCtx);
                if (this.temporary) {
                    this.utils.gridSize.persistTemporaryCells(this.generatedGrid);
                    this.clearTemporaryObject();
                }
                this.scrollHandler.isDragging = null;
                this.dragging = null;
                this.pluginDragHandler = null;
            }
            this.draggingLabel = null;
        }
    }

    /**
     * @description Handler for mouse up event - window - cursor is outside canvas - dragging function active
     * @returns { void }
     */
    mouseUpWindowHandler() {
        this.cellIndependentPluginHandler.mouseDrop();
        if (this.dragging) {
            this.mouseUpHandler(this.dragging);
        } else if (this.pluginDragHandler) this.pluginDragHandler.cancelDrag();
        this.scrollHandler.isDragging = null;
        // clear dragHover cells
        this.generatedGrid.flat(2).forEach(cell => {
            if (cell && cell.state.dragHover) {
                cell.update({}, { dragHover: false });
                cell.draw();
            }
        });
        this.dragging = null;
        this.pluginDragHandler = null;
        this.cellIndependentExpandable.cancelExpanding();

        this.clearTemporaryGrid();
    }

    /**
     * @description - Handler for Drag Over event
     * @param {Object} payload -  contains mouse position over the canvas + the top and left scroll values
     * @returns { void }
     */
    dragOverHandler(payload) {
        if (!this.firstDragOverEventData) {
            this.firstDragOverEventData = payload;
            this.stopLeftScroll = true;
        }

        this.setFocusOnDiagram();
        this.dragging = payload;

        if (payload.hasOwnState && payload.width && payload.height) {
            this.cellIndependentPluginHandler.mouseMoveHandler(payload, null, this.generatedGrid);
        }
    }

    /**
     * @description sets focus on the canvas element
     */
    setFocusOnDiagram() {
        const hasFocus = document.activeElement === this.canvasCtx.canvas;
        if (!hasFocus) this.canvasCtx.canvas.focus();
    }

    /**
     * @description Handler for drop event
     * @param {Object} payload -  contains mouse position over the canvas + the top and left scroll values
     * @returns { void }
     */
    dropHandler(payload) {
        this.firstDragOverEventData = null; // reset firstDragOverEventData on dropHandler
        this.cellIndependentPluginHandler.mouseDrop();
        const dragVal = { ...this.dragging };
        this.dragging = null;
        if (this.temporary) this.utils.gridSize.persistTemporaryCells(this.generatedGrid);
        const plugin = this.utils.pluginRetrieval.getPluginFromDiagram(payload, this.fabricDiagramCtx);
        this.resetLastSelectedStyleIfValidDrop(dragVal, this.generatedGrid);
        if (payload.hasOwnState) {
            this.cellIndependentPluginHandler.createDroppedPlugin(plugin.plugin, plugin.id, dragVal, this.generatedGrid, this.canvasCtx);
        } else {
            this.selectionWrapper.resetSpecific(["CELL_INDEP"]);
            this.handleCellPluginDrop(dragVal, plugin, payload);
        }
        this.autoScrollIntervalId = clearInterval(this.autoScrollIntervalId);
        this.autoScrollCounter = 0;
        this.clearTemporaryObject();
    }

    /**
     * @description Handles the drop action of a plugin on the diagram
     * @param { Object } dragVal - {x, y} object containing mouse position
     * @param { Object } plugin  - {plugin:ConstructorFunction, id:String }. plugin property is a plugin constructor function and id is the id of the plugin
     * @param { Object } payload - {pluginId:String } the plugin id
     * @return { void }
     */
    handleCellPluginDrop(dragVal, plugin, payload) {
        const oldState = this.getDiagramData();
        const newSelected = this.utils.droppedPluginCreator.createDroppedPlugin(dragVal, plugin, this.generatedGrid, this.connectingLineHandler);
        if (newSelected == null) {
            const cellPos = this.utils.gridSize.getPermanentGridCellPos(dragVal, this.generatedGrid);
            const cell = this.utils.cellRetrieval.getCell(cellPos, this.generatedGrid);
            if (cell) {
                cell.update({}, { dragHover: false });
                cell.draw();
            }
            this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.INVALID_PLUGIN_DROP, { PLUGIN_ID: plugin.id });
            return;
        }

        const newCell = this.utils.cellRetrieval.getCell(newSelected, this.generatedGrid);
        const newCellContent = newCell.getContentInstance();
        // for user
        this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.PLUGIN_CREATED, { PLUGIN_ID: payload.pluginId, PLUGIN_UUID: newCellContent.uuid, PLUGIN_LABEL: newCellContent.name }, false, true);
        // for diagram state holder
        const newLabel = newCell.labelHandler.labelsList.find(label => label.cellInstance === newCell);
        this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.MULTIPLE_ACTIONS_HAPPENED, [
            { event: DIAGRAM_EMIT_EVENTS.PLUGIN_CREATED, oldState, payload: { PLUGIN_ID: payload.pluginId, PLUGIN_UUID: newCellContent.uuid, PLUGIN_LABEL: newCellContent.name } },
            {
                event: DIAGRAM_EMIT_EVENTS.PLUGIN_LABEL_CREATED,
                oldState,
                payload: {
                    LABEL_UUID: newLabel.uuid, LABEL_VALUE: newLabel.label, PLUGIN_UUID: newLabel.pluginUUID, PLUGIN_ID: newLabel.pluginID
                }
            }
        ], true, false);
        this.clearTemporaryObject();
    }

    /**
     *
     * @param {Object} pos -  {x,y} mouse position
     * @param {Object} grid  - the grid matrix holding the GridCell references
     */
    resetLastSelectedStyleIfValidDrop(pos, grid) {
        const cellPos = this.utils.gridSize.getPermanentGridCellPos(pos, grid);
        const cell = this.utils.cellRetrieval.getCell(cellPos, grid);
        if (cell && !cell.hasContent()) {
            this.cellSelection.updatePreviouslySelectedCell(this.generatedGrid);
        }
    }

    /**
     * @description  this is used in case the grid was expanded for a plugin move and the element was then !!!! moved, resized or deleted !!!!
     * doesnt delete beyond the minimum grid size, but makes sure it deletes no longer used cells that were generated extra
     */
    checkForGridReduction(oldPosition, oldSize) {

    }

    /**
     * @description Handles the delete key down event. Deletes the selected plugin
     * @return { void }
     */
    keyDownHandlerDelete() {
        const selectedCell = this.cellSelection.getSelectedCellContent(this.generatedGrid);
        const selectedCellIndependentPlugin = this.cellIndependentPluginHandler.getSelected();
        const selectedLine = this.lineSelection.getSelected();
        if (selectedCell) {
            const cell = this.connectingLineHandler.getIncommingOutgoingInstance(selectedCell.uuid);
            if (cell) {
                cell.outgoing.forEach(element => {
                    this.labelHandler.deleteConnectorsLine(element);
                });
                cell.incoming.forEach(element => {
                    this.labelHandler.deleteConnectorsLine(element);
                });
            }
            const apiHandler = this.getApiHandlerInstance();
            apiHandler.removePlugin(selectedCell, this);
            this.connectingLineHandler.redrawRequest(selectedCell.uuid, this.labelHandler);
            this.labelHandler.deleteLabel(selectedCell.uuid, this.generatedGrid, this.canvasCtx);
        }
        if (selectedLine) {
            const apiHandler = this.getApiHandlerInstance();
            apiHandler.removeConnector(selectedLine);
            this.labelHandler.deleteConnectorsLine(selectedLine);
        }
        if (selectedCellIndependentPlugin) {
            const { id } = selectedCellIndependentPlugin.data;
            const deletingItemUuid = selectedCellIndependentPlugin.uuid;
            const pluginData = this.utils.pluginRetrieval.getPluginFromDiagram(id, this.fabricDiagramCtx);
            const isContainer = !!pluginData.defaultSize;
            if (isContainer) {
                this.cellIndependentPluginHandler.removeContainer(deletingItemUuid);
            } else {
                const parentUuid = this.cellIndependentPluginHandler.getParentUuidFromChildUuid(deletingItemUuid);
                this.cellIndependentPluginHandler.removeChild(parentUuid, deletingItemUuid);
            }
        }
    }

    /**
     * @description - Handles the drag leave event.
     */
    dragLeaveHandler() {
        this.dragging = null;
        this.clearTemporaryGrid();
        this.autoScrollIntervalId = clearInterval(this.autoScrollIntervalId);
        this.autoScrollCounter = 0;
    }

    /**
     * @description Returns true / false if the mouse is currently being hovered over a clicabkle element
     *              Used inside FabricDiagram to set the cursor type over the diagram
     * @param {Object} mousePos - {x,y} object representing current mosue position
     * @returns { Boolean }
     */
    isClickableElement(mousePos) {
        // check if is over a connector
        const isLine = this.lineSelection.checkIfLineWasClicked(mousePos);
        if (isLine) return true;
        // check if is over a cell based plugin
        const insideCellBasedPlugin = (() => {
            const cellPos = this.utils.cellRetrieval.getCellPosition(mousePos, null, this.generatedGrid);
            const cell = this.utils.cellRetrieval.getCell(cellPos, this.generatedGrid);
            return cell && !!cell.hasContent();
        })();
        if (insideCellBasedPlugin) return true;
        // check if is over a cell independent plugin
        const insideCellIndep = this.cellIndependentPluginHandler.mousePositionHoveringCellIndependentPlugin(mousePos);
        if (insideCellIndep) return true;
        // check if is over a anchored plugin
        const clickedAnchoredPlugin = this.anchoredPluginsHandler.clickedAnchoredPlugin(mousePos);
        if (clickedAnchoredPlugin) return true;
        // check if is over a label
        const isLabel = this.labelHandler.checkIfLabelSelected(mousePos, this.generatedGrid) || this.labelHandler.checkIfLabelConnector(mousePos);
        if (isLabel) return true;
    }

    /**
     * @description mouse down handler
     * @param {Object} payload - {x,y} object containig mouse position
     *
     */
    mouseDownHandler(payload) {
        const clickedAnchoredPlugin = this.anchoredPluginsHandler.clickedAnchoredPlugin(payload);
        this.selectionWrapper.resetSpecific(["LINE"]);

        if (clickedAnchoredPlugin) {
            if (!clickedAnchoredPlugin.dataCustomAchored && !this.oneAtATime) {
                delete clickedAnchoredPlugin.dataCustomAchored;
                this.selectionWrapper.setSelected("ANCHORED_ELEM", clickedAnchoredPlugin);
                this.selectionWrapper.resetSpecific(["LINE"]);
                return;
            }
        } else {
            this.selectionWrapper.resetSpecific(["ANCHORED_ELEM"]);
        }
        const previousSelectedCell = this.cellSelection.getSelected();
        let cell; let clickedElem; const selectedCell = !this.oneAtATime ? this.cellSelection.handleClickSelection(payload, this.generatedGrid) : null;
        const newCellPos = this.utils.gridSize.getPermanentGridCellPos(payload, this.generatedGrid);
        const checkIfLabel = this.labelHandler.checkIfLabelSelected(payload, this.generatedGrid);
        const checkIfConnectorLabel = this.labelHandler.checkIfLabelConnector(payload);
        const expandPointOnCellIndependentPlugin = this.cellIndependentPluginHandler.checkIfIsExpandPoint(payload, this.generatedGrid);
        const movePointOnCellIndependentPlugin = this.cellIndependentPluginHandler.checkIfIsMovePoint(payload, this.generatedGrid);
        const openHeaderMenu = this.cellIndependentPluginHandler.checkIfOpenMenu(payload, this.generatedGrid);
        if (this.readonly) {
            this.cellIndependentPluginHandler.checkIfIsMoreItemsBlade(payload, this.generatedGrid);
        }

        if ((checkIfLabel || checkIfConnectorLabel) && !this.readonly) {
            const scope = this;
            checkIfLabel ? scope.labelHandler.selectedLabelConnect = null : scope.labelHandler.selectedLabel = null;
            scope.draggingLabel = true;
            scope.dragging = null;
            scope.labelHandler.labelCell = scope.utils.cellRetrieval.getCell(newCellPos, scope.generatedGrid);
            scope.cellSelection.updatePreviouslySelectedCell(scope.generatedGrid);
            scope.selectionWrapper.resetSpecific(["LINE"]);
        } else if (expandPointOnCellIndependentPlugin && !this.readonly) {
            this.cellIndependentExpandable.triggeringExpandingPlugin(expandPointOnCellIndependentPlugin, payload, this.generatedGrid);
            this.selectionWrapper.setSelected("CELL_INDEP", { parent: expandPointOnCellIndependentPlugin.instance.uuid });
            this.selectionWrapper.resetSpecific(["LINE"]);
            return false;
        } else if (openHeaderMenu && !this.readonly) {
            this.cellIndependentPluginHandler.openHeaderMenu(openHeaderMenu, payload);
            this.selectionWrapper.setSelected("CELL_INDEP", { parent: openHeaderMenu.instance.uuid });
            this.selectionWrapper.resetSpecific(["LINE"]);
            return false;
        } else {
            if (selectedCell) {
                cell = this.utils.cellRetrieval.getCell(selectedCell, this.generatedGrid);
                const connectingLineHandler = new ConnectingLineHandler(this.instanceUuid);
                clickedElem = connectingLineHandler.getClickedCellElement(cell.uuid, payload);
                this.selectionWrapper.resetSpecific(["LINE", "CELL_INDEP"]);
            } else {
                this.selectionWrapper.resetSpecific(["CELL", "LINE"]);
                if (!expandPointOnCellIndependentPlugin && !movePointOnCellIndependentPlugin && !this.cellIndependentPluginHandler.childrenClickCheck(payload, this.generatedGrid)) {
                    this.selectionWrapper.resetSpecific(["CELL_INDEP"]);
                }
                this.lineSelection.handleClickSelection(payload, this.labelHandler);
            }
            if (clickedElem && clickedElem.type === GRID_CELL_CLICKABLE_ELEMENTS.DRAGGABLE_ENDPOINT && previousSelectedCell && previousSelectedCell.row === selectedCell.row && previousSelectedCell.col === selectedCell.col && !this.readonly) {
                this.connectingLineDrag.startDrag(payload, cell, selectedCell, this.generatedGrid, clickedElem);
                this.selectionWrapper.resetSpecific(["CELL_INDEP"]);
                return;
            }
            if (!selectedCell) this.mouseMoveSelectedCellHandler.resetHover();
            this.scrollHandler.isDragging = !!selectedCell || !!movePointOnCellIndependentPlugin;
            if (this.scrollHandler.isDragging) {
                this.dragging = { x: payload.x, y: payload.y };
                if (selectedCell) {
                    this.pluginDragHandler = new PluginDragHandler(this.generatedGrid, cell);
                    this.selectionWrapper.resetSpecific(["CELL_INDEP", "LINE"]);
                } else {
                    const pluginSize = movePointOnCellIndependentPlugin.instance.size;
                    this.pluginDragHandler = new CellIndependentMoveHandler(movePointOnCellIndependentPlugin, payload);
                    this.selectionWrapper.setSelected("CELL_INDEP", movePointOnCellIndependentPlugin.instance.uuid);
                    if (this.pluginDragHandler.mouseLeftDraggedPluginInitialPosition(payload)) {
                        this.dragging.width = pluginSize.width;
                        this.dragging.height = pluginSize.height;
                        this.dragging.hasOwnState = true;
                        this.dragging.pluginUuid = movePointOnCellIndependentPlugin.instance.uuid;
                    }
                }
            }
            this.draggingLabel = false;
        }
        this.cellIndependentPluginHandler.handleClickSelection(payload, this.generatedGrid, this.cellSelection, this.lineSelection);
    }

    resetDiagramScroll() {
        this.camera.scrollbars.scrollBy(20, 0);
        this.camera.scrollbars.scrollBy(-20, 0);
    }

    /**
     * @description - Double click event handler
     * @param {Object} payload  - {x,y} object containing mouse coordonates
     */
    mouseDblClickHandler(payload) {
        if (!this.readonly) {
            const selectedLine = this.lineSelection.getSelected();
            const newLabel = new LabelCell(payload, this.labelHandler); const
                scope = this;
            if (selectedLine && this.labelHandler.checkIfIsNewLabel(selectedLine)) {
                this.doubleClickActionOn = true;
                const selectedLineData = this.lineSelection.getSelectedLineData();
                this.selectionWrapper.handleDoubleClickSelection();
                newLabel.editConnectorlabel(selectedLineData, payload, this.connectingLineHandler.connectors, this.doubleClickActionOn);
                setTimeout(() => {
                    scope.doubleClickActionOn = false;
                }, 20);
            } else {
                this.doubleClickActionOn = true;
                this.cellSelection.handleDoubleClickSelection(payload, this.generatedGrid);
                newLabel.editLabel(payload, this.generatedGrid, this.config.cellWidth, this.doubleClickActionOn);
                setTimeout(() => {
                    scope.doubleClickActionOn = false;
                }, 20);
            }
        } else {
            this.doubleClickActionOn = false;
        }
    }

    /**
     * @description Performs grid animation using frame animations
     * @param {GridView} _self
     */
    gridAnimations(_self) {
        const ref = {};
        let lastHoveredCell = null;
        let lastGeneratedTempCells = null;

        function animateGridCells() {
            requestAnimationFrame(animateGridCells);
            // Hover Animation handler
            if (!_self.readonly && !_self.destroying) {
                const vals = _self.utils.cellHighlight.handleHoverAnimation(_self, lastHoveredCell, lastGeneratedTempCells);
                lastHoveredCell = vals.cells;
                lastGeneratedTempCells = vals.tempCells;
                ref.lastHoveredCell = vals.cells;
                ref.lastGeneratedTempCells = vals.tempCells;
            }
            if (_self.dragging && _self.readonly === false) {
                _self.sidesDragScroll(_self.dragging);
            }
        }
        animateGridCells();
        return ref;
    }

    /**
     * @description - Auto scrolls the camera if there is a drag event and the mouse position is close to a edge
     * @param {Object} payload - {x,y} object containing mouse coordonates
     */
    sidesDragScroll(payload) {
        const viewportHeight = this.camera.getVisibleArea().height;
        const viewportWidth = this.camera.getVisibleArea().width;
        const existingZoom = this.fabricDiagramCtx.zoom - 1 > 0 ? this.fabricDiagramCtx.zoom - 1 : 0;
        const existingZoomIn = this.fabricDiagramCtx.zoom < 1;
        const scrollBy = 20;

        if (!this.cursorIsOutsideCanvas) {
            if (payload.x <= (this.camera.lastViewport.left + 100) && !this.stopLeftScroll) {
                if (this.temporary && this.temporary.prevCamera && this.temporary.prevCamera.left >= (scrollBy + 1)) {
                    this.temporary.prevCamera.left += -scrollBy;
                }
                if (this.camera.scrollbars.left > (scrollBy + 1)) {
                    this.camera.scrollbars.scrollBy(-scrollBy, 0);
                }
            }

            if (payload.y <= (existingZoomIn ? ((this.camera.lastViewport.top + 100) / this.fabricDiagramCtx.zoom) : this.camera.lastViewport.top + 100)) {
                if (this.temporary && this.temporary.prevCamera && this.temporary.prevCamera.top >= (scrollBy + 1)) {
                    this.temporary.prevCamera.top += -scrollBy;
                }
                if (this.camera.scrollbars.top >= (scrollBy + 1)) {
                    this.camera.scrollbars.scrollBy(0, -scrollBy);
                }
            }

            if ((existingZoomIn ? viewportHeight / this.fabricDiagramCtx.zoom : viewportHeight) - payload.y < (existingZoom ? (viewportHeight * existingZoom) : 200)) {
                if (this.temporary && this.temporary.prevCamera) {
                    this.temporary.prevCamera.top += 20;
                }
                this.camera.scrollbars.scrollBy(0, 20);
            }

            if (viewportWidth - payload.x < (existingZoom ? viewportWidth * existingZoom : 200)) {
                if (this.temporary && this.temporary.prevCamera) {
                    this.temporary.prevCamera.left += 20;
                }
                this.camera.scrollbars.scrollBy(20, 0);
            }
        }
    }
    }
    return GridView;
};
